import React, { useState, useMemo, useEffect } from 'react';
import { get, find, uniqBy } from 'lodash';
import styled from 'styled-components';
import { withBreakpoints } from 'react-breakpoints';
import { connect } from 'react-redux';
import { Grid, Row, Col } from 'react-bootstrap';
import ReactMapboxGl, { Layer, Source, Popup, Feature } from 'react-mapbox-gl';

import StyledSelect from 'components/atoms/inputs/Select';
import ContentTile from 'components/atoms/layout/ContentTile';
import ContentHeader from 'components/atoms/layout/ContentHeader';
import ChannelTitle from 'components/atoms/layout/ChannelTitle';
import HyperlocalTable from 'components/molecules/tables/labs/HyperlocalTable';
import { fetchMeta } from 'services/entities/duck';
import { fetchHyperLocal, fetchHyperLocalCampaigns, fetchHyperLocalZipData } from 'services/hyperlocal/duck';
import { formatNumber } from 'helpers/formatNumbers';
import { NoDataTile } from 'components/molecules/ErrorTile';


import FilterBar from 'components/atoms/tables/FilterBar';
import FilterLabel from 'components/atoms/tables/FilterLabel';
import ToggleSwitch from 'components/atoms/form/ToggleSwitch';

import chroma from 'chroma-js';

const IMPRESSION_LIMIT = 1000;

const layoutLayer = {
    'icon-image': 'mapMarkerImage',
    'icon-allow-overlap': true
};

const StyledPopup = styled.div`
  background: white;
  color: #3f618c;
  font-weight: 400;
  padding: 5px;
  border-radius: 2px;
`;

const LegendBar = styled.div`
  height: ${props => props.height}%;
  background-image: linear-gradient(
    to bottom,
    ${props => props.colors[0]},
    ${props => props.colors[1]}
  );
  width: 20px;
  &:hover {
    opacity: 0.8;
  }
`;

const LegendContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: center;

    position: absolute;
    top: 20px;
    height: 300px;
    right: 8px;
`;

const Select300 = styled(StyledSelect)`
    width: 300px;
`;

const CampaignDropdown = ({ labelName, onChange, selectedId, children }) => (
    <div style={{ marginBottom: '22px' }}>
        <label style={{ margin: '0 5px 0 5px' }}>{labelName}</label>
        <Select300 value={selectedId} onChange={onChange}>
            {children}
        </Select300>
    </div>
);

const CopyPadding = styled.div`
    padding: 10px 10px 10px 15px;
`;

const TRANSPARENT_RGBA = 'rgba(0, 0, 0, 0)';
// Bounds from https://docs.mapbox.com/mapbox-tiling-service/examples/bbox/
const US_MAP_BOUNDS = [[-125.0011, 24.9493], [-66.9326, 49.5904]];

const HEATMAP_METRIC_OPTIONS = [
    {
        label: 'Conversion Rate',
        accessor: 'conversion_rate'
    },
    {
        label: 'Conversions',
        accessor: 'conversions'
    },
    {
        label: 'Impressions',
        accessor: 'impressions'
    },
    {
        label: 'Budget',
        accessor: 'spend'
    },
    {
        label: 'CPA',
        accessor: 'cpa'
    },
];

const zipColorScale = chroma.scale(['007DC6', 'D04328', 'FBAD19']);

const getZipMatcher = (zipData, selectedMetric) => {
    const dataForSelectedMetric = zipData.map(z => z[selectedMetric]);
    const minValue = Math.min.apply(null, dataForSelectedMetric);
    const maxValue = Math.max.apply(null, dataForSelectedMetric);

    const getZipColor = zipColorScale.domain([minValue, maxValue]);

    const matchExpression = ['match', ['get', 'ZIP5']];

    zipData.forEach((zip) => {
        const rgba = getZipColor(zip[selectedMetric]).rgb();
        matchExpression.push(zip.zipcode, `rgb(${rgba[0]}, ${rgba[1]}, ${rgba[2]})`);
    });

    // default color if not matched.
    matchExpression.push(TRANSPARENT_RGBA);
    return matchExpression;
};

const Map = ReactMapboxGl({
    accessToken: get(window, 'goodway.REACT_APP_MAPBOX_KEY', process.env.REACT_APP_MAPBOX_KEY)
});

const createPayload = entityId => ({
    transactionId: `entity-${entityId}-lab-hyperlocal`,
    entityId
});


const LabsHyperlocalContainer = (props) => {
    const [zipInfo, setZipInfo] = useState({ zipData: {} });
    const [campaign, setCampaign] = useState({ campaign: {} });
    const [selectedMetric, setSelectedMetric] = useState('conversion_rate');
    const [mapZoom] = useState([10]);
    const [activeMapMaker, setActiveMapMaker] = useState({});
    const [showAllLocations, setShowAllLocations] = useState(true);
    const entityId = get(props, 'meta.id', null);

    const hasHyperLocalData = get(props, 'hyperLocal.hasData', false);
    const mapMarkers = get(props, 'hyperLocal.data.mapMarkers', {});

    const hasHyperLocalZips = get(props, 'hyperLocal.zipData.hasData', false);
    const zipsByCampaigns = get(props, 'hyperLocal.zipData.data', false);
    const basePath = 'mti-etl/development/clean';

    const hyperLocalCampaigns = useMemo(() => {
        if (!hasHyperLocalZips) {
            return [];
        }

        const campaigns = Object.keys(zipsByCampaigns).map(key => ({
            id: key,
            name: get(zipsByCampaigns, [key, 0, 'campaign_name'])
        }));

        return campaigns;
    }, [hasHyperLocalZips, zipsByCampaigns]);

    if (hyperLocalCampaigns.length > 0 && !campaign.id) {
        setCampaign(hyperLocalCampaigns[0]);
    }

    useEffect(() => {
        // TODO: For the POC we only support Kubota as we hand created only the Kubota locations file.
        // If this POC is sucessfull we need to have a more dynamic way of getting franchise locations.
        // We should also switch from the TTD id in the filename. Switch it to a Brand ID?
        const payload = [
            {
                key: 'mapMarkers',
                path: `${basePath}/hyperlocal_suite/ui/map_locations_7syif2s.json`
            }
        ];
        props.fetchHyperLocal(payload);
    }, [entityId]);

    useEffect(() => {
        setCampaign({});
        props.fetchHyperLocalZipData(createPayload(entityId));
    }, [entityId, props.selectedTimeRange]);


    const zipData = useMemo(() => {
        if (!hasHyperLocalZips || !campaign || !campaign.id) {
            return [];
        }

        const data = uniqBy(get(zipsByCampaigns, [campaign.id], []), 'zipcode');
        return data;
    }, [hasHyperLocalZips, zipsByCampaigns, campaign]);

    const onMapClick = () => {
        setZipInfo({
            zip: '00000',
            coordinates: null,
            zipData: {}
        });
    };

    const onMapMarkerClick = (mapMaker) => {
        if (get(mapMaker, 'DealerNumber') === get(activeMapMaker, 'DealerNumber')) {
            setActiveMapMaker(null);
        } else {
            setActiveMapMaker(mapMaker);
        }
    };

    let bounds = useMemo(() => {
        const cords = zipData.reduce((acc, zip) => {
            acc.lat.push(zip.latitude);
            acc.lon.push(zip.longitude);
            return acc;
        }, { lat: [], lon: [] });

        cords.lat = cords.lat.filter(x => x);
        cords.lon = cords.lon.filter(x => x);

        if (cords.lat.length === 0 || cords.lon === 0) {
            return [];
        }

        return [
            [Math.min.apply(null, cords.lon), Math.min.apply(null, cords.lat)],
            [Math.max.apply(null, cords.lon), Math.max.apply(null, cords.lat)]
        ];
    }, [zipData]);

    if (bounds.length === 0) {
        bounds = US_MAP_BOUNDS;
    }

    const mapImages = useMemo(() => {
        if (mapMarkers.marker) {
            const mapMarkerImage = new Image();
            mapMarkerImage.src = mapMarkers.marker.imageSource;
            return ['mapMarkerImage', mapMarkerImage];
        }
        return [];
    }, [mapMarkers]);

    return <Grid fluid>
        <ChannelTitle>
            Hyperlocal Studio
        </ChannelTitle>

        <Row>
            <Col sm={12} lg={8}>
                <ContentTile>
                    <ContentHeader hideCSVExport={true}>About this Lab</ContentHeader>
                    <CopyPadding>
                        The goal of this lab is to empower an understanding of zip-level market
                        insights relative to a campaign across all programmatic tactics.
                        By augmenting campaign data with external
                        data, uncover what makes prospects in a specific geo unique compared to
                        National and Regional prospects. With this information, we can demonstrate
                        our local market knowledge to clients, while fueling strategic market level
                        optimizations to help boost campaign performance.
                    </CopyPadding>
                </ContentTile>
            </Col>
        </Row>


        {hasHyperLocalZips && hyperLocalCampaigns.length > 0 &&
            <CampaignDropdown labelName="Campaigns:"
                selectedId={campaign.id} onChange={(event) => {
                    setCampaign(hyperLocalCampaigns.find(element => element.id === event.target.value));
                }}>
                {
                    hyperLocalCampaigns.map(hyperCampaign =>
                        <option key={hyperCampaign.id} value={hyperCampaign.id}>{hyperCampaign.name}</option>
                    )
                }
            </CampaignDropdown>
        }

        {hasHyperLocalZips && campaign.id &&
        <React.Fragment>
            <ContentTile>
                <ContentHeader hideCSVExport={true}>Zip Code Map</ContentHeader>
                <FilterBar>
                    <ToggleSwitch onClick={() => setShowAllLocations(prev => !prev)} value={!!showAllLocations} scale="0.5" />
                    <FilterLabel>Show All Locations</FilterLabel>
                </FilterBar>

                <Map
                    style="mapbox://styles/mapbox/streets-v8"
                    containerStyle={{ height: '400px' }}
                    zoom={mapZoom}
                    onClick={onMapClick}
                    fitBounds={bounds}
                    fitBoundsOptions={{
                        padding: 20
                    }}
                >

                    <Source id="zips" tileJsonSource={{
                        type: 'vector',
                        url: 'mapbox://jn1532.2z2q31r2'
                    }} />
                    <Layer type="vector" id="zipline" sourceId="zips"
                        type="line"
                        sourceLayer='zip5_topo_color-2bf335'
                        layout={{
                            visibility: 'none',
                            'line-join': 'round',
                            'line-cap': 'round'
                        }}
                        paint={{
                            'line-color': 'blue',
                            'line-width': 0.5,
                            'line-opacity': 0.2
                        }}
                    />

                    {hasHyperLocalZips &&
                        <Layer
                            id='Zip'
                            type='fill'
                            sourceId='zips'
                            paint={{
                                'fill-outline-color': 'blue', // '#696969',
                                'fill-color': getZipMatcher(zipData, selectedMetric),
                                'fill-opacity': 0.65
                            }}
                            sourceLayer='zip5_topo_color-2bf335'
                            filter={[
                                'match',
                                ['get', 'ZIP5'],
                                // 1K impression limit to show zip
                                zipData.map(zip => (zip.impressions >= IMPRESSION_LIMIT ? zip.zipcode : null))
                                    .filter(x => x),
                                true,
                                false
                            ]}
                            onMouseMove={(evt) => {
                                const info = evt.features[0].properties;
                                setZipInfo({
                                    zip: info.ZIP5,
                                    coordinates: [evt.lngLat.lng, evt.lngLat.lat],
                                    zipData: find(zipData, ['zipcode', info.ZIP5]) || {}
                                });
                            }}
                        />
                    }

                    {hasHyperLocalData &&
                        <Layer type="symbol" id="marker" layout={layoutLayer} images={mapImages} >
                            {showAllLocations && mapMarkers.locations.map((location, index) => (
                                <Feature key={location.DealerNumber || index}
                                    coordinates={[location.Longitude, location.Latitude]}
                                    onClick={() => onMapMarkerClick(location)}
                                />
                            ))}
                        </Layer>
                    }

                    {activeMapMaker && showAllLocations && (
                        <Popup coordinates={[activeMapMaker.Longitude || 0, activeMapMaker.Latitude || 0]} offset={10} style={{ visibility: activeMapMaker.DealerName ? 'visible' : 'hidden' }}>
                            <StyledPopup>
                                <h5>{activeMapMaker.DealerName}</h5>
                                <div className="adr">
                                    <div className="street-address">{activeMapMaker.Street}</div>
                                    <span className="locality">{activeMapMaker.City}</span>, <abbr
                                        className="region">{activeMapMaker.StateCode}</abbr>, <span
                                        className="postal-code">{activeMapMaker.Zip}</span>
                                </div>
                            </StyledPopup>
                        </Popup>
                    )}

                    {zipInfo && (
                        <Popup coordinates={zipInfo.coordinates || [0, 0]} offset={10} style={{ visibility: zipInfo.coordinates ? 'visible' : 'hidden' }}>
                            <StyledPopup>
                                <div className="text-center">{zipInfo.zip}</div>
                                <div className="text-center">{zipInfo.zipData.city}<br /></div>
                                {hasHyperLocalData &&
                                    <React.Fragment>
                                        <div>Conversions: {
                                            formatNumber(zipInfo.zipData.conversions)
                                        }
                                        </div>
                                        <div>Impressions: {
                                            formatNumber(zipInfo.zipData.impressions)
                                        }
                                        </div>
                                        <div>Conversion Rate: {
                                            `${(Math.round(zipInfo.zipData.conversion_rate * 100000)
                                                / 1000).toFixed(2)}%`}
                                        </div>
                                        {/* TODO: Implement CPM and CPA once data is in the dataset */}
                                        {/* <div>CPM: ${
                                            (Math.round(zipInfo.zipData.conversion_rate * 100000)
                                                    / 1000).toFixed(3)}
                                        </div>
                                        <div>CPA: ${
                                            (Math.round(zipInfo.zipData.conversion_rate * 100000)
                                                / 1000).toFixed(3)}
                                        </div> */}
                                    </React.Fragment>
                                }
                            </StyledPopup>
                        </Popup>
                    )}
                </Map>


                <LegendContainer >
                    <StyledSelect value={selectedMetric} onChange={e => setSelectedMetric(e.target.value)}>
                        {HEATMAP_METRIC_OPTIONS.map(metric =>
                            <option key={metric.accessor} value={metric.accessor}>{metric.label}</option>
                        )}
                    </StyledSelect>
                    <LegendBar height={30} colors={['#FBAD19', '#D04328']} title="high" />
                    <LegendBar height={30} colors={['#D04328', '#007DC6']} title="low" />
                </LegendContainer>
            </ContentTile>
            <HyperlocalTable data={zipData}></HyperlocalTable>
        </React.Fragment>
        }

        {hasHyperLocalZips === false &&
            <NoDataTile>There is no data to show</NoDataTile>
        }
    </Grid >;
};

const mapStateToProps = state => ({
    theme: state.theme,
    meta: get(state, 'entities.meta', {}),
    selectedTimeRange: state.config.selectedTimeRange,
    mapboxKey: state.config.mapboxKey,
    hyperLocal: state.hyperLocal
});

export default connect(mapStateToProps, {
    fetchMeta,
    fetchHyperLocal,
    fetchHyperLocalCampaigns,
    fetchHyperLocalZipData
})(withBreakpoints(LabsHyperlocalContainer));



// WEBPACK FOOTER //
// ./src/containers/labs/LabsHyperlocalContainer.js